import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2, Printer, FileDown, CheckCircle2, Pencil, ChevronDown, ChevronUp } from "lucide-react";
import { exportDoc } from "../../pages/ExamSessionPage";

const mdToHtml = (md) => (md || "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;")
  .replace(/^### (.*)$/gm, "<h3>$1</h3>")
  .replace(/^## (.*)$/gm, "<h2>$1</h2>")
  .replace(/^# (.*)$/gm, "<h1>$1</h1>")
  .replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>")
  .replace(/^\s*[-*] (.*)$/gm, "<li>$1</li>")
  .replace(/^(\d+)[.)] (.*)$/gm, "<p style='margin:2px 0'>$1. $2</p>")
  .replace(/^[─═\-_]{4,}$/gm, "<hr/>")
  .replace(/\n\n/g, "<br/>");

const printSubject = (sujet) => {
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>${sujet.title}</title>
    <style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;line-height:1.5}h1{font-size:20px;border-bottom:2px solid #000;padding-bottom:8px}h2{font-size:16px;margin-top:24px}h3{font-size:13px;background:#eee;padding:4px 8px}li,p{font-size:12px}</style>
    </head><body>${mdToHtml(sujet.content)}</body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 400);
};

export default function SubjectsTab({ api, session, cert, onChange }) {
  const [nbSujets, setNbSujets] = useState(cert.pratique?.nb_sujets || 3);
  const [contexte, setContexte] = useState(session.contexte_materiel || cert.materiel || "");
  const [generating, setGenerating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [showPicker, setShowPicker] = useState((session.sujets || []).length === 0);

  const sujets = session.sujets || [];
  const isClosed = session.statut === "cloturee";

  const generate = async () => {
    if (sujets.length > 0 && !window.confirm(`${sujets.length} sujet(s) existent déjà et seront REMPLACÉS par la nouvelle génération. Continuer ?`)) return;
    setGenerating(true);
    try {
      await api.post(`/certif/sessions/${session.session_id}/generate-subjects`, { nb_sujets: Number(nbSujets) || 3, contexte });
      toast.success("Sujets générés — relisez et validez chaque sujet");
      setShowPicker(false);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de génération"); }
    finally { setGenerating(false); }
  };

  const saveEdit = async (sujetId) => {
    await api.put(`/certif/sessions/${session.session_id}/subjects/${sujetId}`, { content: draft });
    setEditing(null); toast.success("Sujet mis à jour"); onChange();
  };
  const validate = async (sujetId) => {
    await api.put(`/certif/sessions/${session.session_id}/subjects/${sujetId}`, { status: "valide" });
    toast.success("Sujet validé"); onChange();
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="subjects-generator">
        {!showPicker && sujets.length > 0 ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-white">{sujets.length} sujet(s) générés pour cette session.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => exportDoc(api, session.session_id, "subjects.docx")}
                className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="export-subjects-btn">
                <FileDown className="w-4 h-4 mr-1.5" /> Exporter tous les sujets (DOCX)
              </Button>
              {!isClosed && (
                <Button variant="outline" onClick={() => setShowPicker(true)} className="border-white/10 text-slate-400 hover:bg-white/5" data-testid="reopen-picker-btn">
                  Régénérer…
                </Button>
              )}
            </div>
          </div>
        ) : (
        <>
        <p className="text-sm font-bold text-white mb-1">Génération des sujets de mise en situation</p>
        <p className="text-xs text-slate-500 mb-3">L'IA rédige des sujets distincts (A, B, C…) couvrant toutes les compétences du référentiel, à partir du contexte et du matériel décrits ci-dessous.</p>
        <div className="grid md:grid-cols-[120px_1fr] gap-3 items-start mb-3">
          <div>
            <label className="text-[10px] uppercase text-slate-500">Nb de sujets</label>
            <Input type="number" min={1} max={6} value={nbSujets} onChange={e => setNbSujets(e.target.value)}
              className="bg-[#0b0811] border-white/10 text-white" data-testid="subjects-nb-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500">Contexte / matériel disponible pour cette session</label>
            <Textarea rows={4} value={contexte} onChange={e => setContexte(e.target.value)}
              placeholder="Décrivez le métier, le matériel, les équipements et les contraintes dont disposeront les candidats…"
              className="bg-[#0b0811] border-white/10 text-white text-xs" data-testid="subjects-contexte-input" />
          </div>
        </div>
        <Button onClick={generate} disabled={generating || isClosed}
          className="bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="generate-subjects-btn">
          {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
          {generating ? "Génération IA en cours (30-90s)…" : `Générer ${nbSujets} sujet(s) par IA`}
        </Button>
        {sujets.length > 0 && (
          <Button variant="outline" onClick={() => exportDoc(api, session.session_id, "subjects.docx")}
            className="ml-2 border-white/10 text-slate-300 hover:bg-white/5" data-testid="export-subjects-btn">
            <FileDown className="w-4 h-4 mr-1.5" /> Exporter tous les sujets (DOCX)
          </Button>
        )}
        </>
        )}
      </div>

      {sujets.map(s => (
        <div key={s.sujet_id} className="bg-[#14101c] border border-white/5 rounded-xl" data-testid={`subject-card-${s.letter}`}>
          <div className="flex items-center gap-2 p-4 cursor-pointer" onClick={() => setOpenId(openId === s.sujet_id ? null : s.sujet_id)}>
            <span className="w-8 h-8 rounded-lg bg-[#f7c935] text-[#0b0811] font-black flex items-center justify-center">{s.letter}</span>
            <div className="flex-1">
              <p className="text-sm font-bold text-white">{s.case_label}</p>
              <p className="text-[10px] text-slate-500">Généré le {(s.generated_at || "").slice(0, 16).replace("T", " ")}</p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${s.status === "valide" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid={`subject-status-${s.letter}`}>
              {s.status === "valide" ? "Validé" : "Brouillon"}
            </span>
            {openId === s.sujet_id ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
          </div>
          {openId === s.sujet_id && (
            <div className="border-t border-white/5 p-4">
              <div className="flex gap-2 mb-3 flex-wrap">
                {s.status !== "valide" && (
                  <Button size="sm" onClick={() => validate(s.sujet_id)} className="bg-emerald-600 text-white hover:bg-emerald-700 text-xs" data-testid={`validate-subject-${s.letter}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Valider
                  </Button>
                )}
                <Button size="sm" variant="outline" className="border-white/10 text-slate-300 text-xs"
                  onClick={() => { setEditing(editing === s.sujet_id ? null : s.sujet_id); setDraft(s.content); }} data-testid={`edit-subject-${s.letter}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Éditer
                </Button>
                <Button size="sm" variant="outline" className="border-white/10 text-slate-300 text-xs" onClick={() => printSubject(s)} data-testid={`print-subject-${s.letter}`}>
                  <Printer className="w-3.5 h-3.5 mr-1" /> Imprimer
                </Button>
              </div>
              {editing === s.sujet_id ? (
                <>
                  <Textarea rows={20} value={draft} onChange={e => setDraft(e.target.value)} className="bg-[#0b0811] border-white/10 text-white font-mono text-xs" data-testid={`subject-editor-${s.letter}`} />
                  <Button size="sm" onClick={() => saveEdit(s.sujet_id)} className="mt-2 bg-[#f7c935] text-[#0b0811] font-bold text-xs" data-testid={`save-subject-${s.letter}`}>Enregistrer les modifications</Button>
                </>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_h3]:bg-white/5 [&_h3]:px-2 [&_h3]:py-1 [&_h3]:rounded" data-testid={`subject-content-${s.letter}`}>
                  <ReactMarkdown>{s.content}</ReactMarkdown>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
